import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { ShoppingCart, Send, Plus, Minus, X, Mic, MicOff, Volume2, VolumeX, Sparkles, UtensilsCrossed, ChevronDown, Phone } from 'lucide-react';
import { RichCardRenderer } from '../components/RichCards';
import RealtimeVoiceSession from '../components/RealtimeVoiceSession';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ===== VOICE WAVEFORM VISUALIZER ===== */
function VoiceWaveform({ isActive, color = '#D97706', barCount = 20, height = 48 }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const barsRef = useRef(Array.from({ length: barCount }, (_, i) => ({ h: 0.1, target: 0.1, phase: i * 0.4 })));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const barW = Math.floor(W / barCount);
    const gap = 2;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      barsRef.current.forEach((bar, i) => {
        if (isActive) {
          bar.target = 0.15 + Math.abs(Math.sin(Date.now() * 0.003 + bar.phase)) * 0.85;
        } else {
          bar.target = 0.08 + Math.abs(Math.sin(Date.now() * 0.001 + bar.phase)) * 0.15;
        }
        bar.h += (bar.target - bar.h) * 0.18;
        const bh = Math.max(4, bar.h * H);
        const x = i * barW + gap / 2;
        const y = (H - bh) / 2;
        const r = Math.min(barW / 2 - 1, 4);
        ctx.beginPath();
        ctx.roundRect(x, y, barW - gap, bh, r);
        const alpha = isActive ? 0.7 + bar.h * 0.3 : 0.25 + bar.h * 0.2;
        ctx.fillStyle = color + Math.round(alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [isActive, color, barCount]);

  return (
    <canvas ref={canvasRef} width={barCount * 14} height={height}
      style={{ display: 'block', borderRadius: 8 }} />
  );
}

/* ===== VOICE-FIRST MODE OVERLAY ===== */
function VoiceFirstMode({ isAr, color, rName, siteLogoUrl, onClose, onSendVoice, loading, isSpeaking, messages, avatarState, isRecording, onStartRecord, onStopRecord, thinkingText }) {
  const lastBotMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'linear-gradient(160deg, #FFFBF5 0%, #FFF3DC 40%, #FAFAF9 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
      padding: '28px 20px', maxWidth: 520, margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onClose} style={{ width: 38, height: 38, borderRadius: 12, background: '#F5F5F4', border: '1px solid #E7E5E4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <X style={{ width: 16, height: 16, color: '#78716C' }} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1917' }}>{rName}</div>
          <div style={{ fontSize: 10, color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
            {isAr ? 'وضع صوتي' : 'Voice Mode'}
          </div>
        </div>
        <div style={{ width: 38 }} />
      </div>

      {/* Bot Avatar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, flex: 1, justifyContent: 'center' }}>
        <div style={{
          width: 110, height: 110, borderRadius: 28,
          background: `linear-gradient(135deg, ${color}18, ${color}08)`,
          border: `2px solid ${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: isSpeaking ? `0 0 0 12px ${color}15, 0 0 0 24px ${color}06` : `0 4px 24px ${color}15`,
          transition: 'box-shadow 0.4s ease',
          animation: isSpeaking ? 'speakPulseLight 1.5s ease infinite' : 'none',
        }}>
          {siteLogoUrl ? (
            <img src={siteLogoUrl.startsWith('/') ? `${API.replace('/api', '')}${siteLogoUrl}` : siteLogoUrl} alt="" style={{ width: 80, height: 80, borderRadius: 20, objectFit: 'cover' }} />
          ) : (
            <BotAvatar size={80} state={avatarState} color={color} />
          )}
        </div>

        {/* Waveform */}
        <VoiceWaveform isActive={isRecording || isSpeaking || loading} color={color} barCount={24} height={44} />

        {/* Status text */}
        <div style={{ textAlign: 'center', minHeight: 44 }}>
          {isRecording ? (
            <div style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', animation: 'pulse 1.5s ease infinite' }}>
              {isAr ? '🎙️ جاري التسجيل...' : '🎙️ Recording...'}
            </div>
          ) : loading ? (
            <div style={{ fontSize: 14, fontWeight: 600, color: color }}>
              {thinkingText || (isAr ? '⏳ يفكّر...' : '⏳ Thinking...')}
            </div>
          ) : isSpeaking ? (
            <div style={{ fontSize: 14, fontWeight: 600, color: color }}>
              {isAr ? '🔊 يتكلم...' : '🔊 Speaking...'}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#A8A29E', fontWeight: 500 }}>
              {isAr ? 'اضغط للتحدث' : 'Tap to speak'}
            </div>
          )}
        </div>

        {/* Conversation history - last 2 exchanges */}
        <div style={{ width: '100%', maxWidth: 360, space: 8 }}>
          {lastUserMsg && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
              <div style={{ padding: '10px 14px', borderRadius: '18px 18px 4px 18px', background: `linear-gradient(135deg, ${color}, ${color}E0)`, color: '#fff', fontSize: 13, maxWidth: '80%', lineHeight: 1.5 }}>
                {lastUserMsg.text}
              </div>
            </div>
          )}
          {lastBotMsg && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <div style={{ padding: '10px 14px', borderRadius: '18px 18px 18px 4px', background: '#FFFFFF', border: '1px solid #F0EDEA', color: '#1C1917', fontSize: 13, maxWidth: '80%', lineHeight: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {lastBotMsg.text}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Big Mic Button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <button
          onMouseDown={onStartRecord} onMouseUp={onStopRecord} onMouseLeave={() => isRecording && onStopRecord()}
          onTouchStart={onStartRecord} onTouchEnd={onStopRecord}
          disabled={loading || isSpeaking}
          style={{
            width: 80, height: 80, borderRadius: '50%',
            background: isRecording ? 'linear-gradient(135deg, #EF4444, #DC2626)' : `linear-gradient(135deg, ${color}, ${color}CC)`,
            border: 'none', cursor: loading || isSpeaking ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isRecording ? '0 0 0 8px rgba(239,68,68,0.15), 0 8px 24px rgba(239,68,68,0.3)' : `0 0 0 8px ${color}15, 0 8px 24px ${color}35`,
            transition: 'all 0.3s',
            opacity: loading || isSpeaking ? 0.5 : 1,
            animation: isRecording ? 'recordPulse 1.5s ease infinite' : 'none',
          }}>
          {isRecording ? <MicOff style={{ width: 30, height: 30, color: '#fff' }} /> : <Mic style={{ width: 30, height: 30, color: '#fff' }} />}
        </button>
        <p style={{ fontSize: 11, color: '#A8A29E', fontWeight: 500, textAlign: 'center' }}>
          {isAr ? 'اضغط مع الاستمرار للتحدث' : 'Hold to speak'}
        </p>
      </div>

      <style>{`
        @keyframes speakPulseLight{0%,100%{box-shadow:0 0 0 4px ${color}12,0 4px 20px ${color}20}50%{box-shadow:0 0 0 14px ${color}08,0 4px 20px ${color}30}}
        @keyframes recordPulse{0%,100%{box-shadow:0 0 0 8px rgba(239,68,68,0.15),0 8px 24px rgba(239,68,68,0.3)}50%{box-shadow:0 0 0 20px rgba(239,68,68,0.04),0 8px 24px rgba(239,68,68,0.2)}}
        @keyframes pulse{0%,100%{opacity:0.6}50%{opacity:1}}
      `}</style>
    </div>
  );
}

/* ===== ANIMATED AVATAR ===== */
function BotAvatar({ size = 28, state = 'idle', color = '#D97706' }) {
  // states: idle, talking, thinking, celebrating
  const s = size;
  const eyeSize = s * 0.12;
  const mouthW = s * 0.28;
  return (
    <svg viewBox="0 0 60 60" width={s} height={s} style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="avGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color + 'BB'} />
        </linearGradient>
        <filter id="avGlow">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Head */}
      <circle cx="30" cy="30" r="28" fill="url(#avGrad)" filter="url(#avGlow)" />
      {/* Chef hat */}
      <ellipse cx="30" cy="12" rx="16" ry="8" fill="#fff" opacity="0.9" />
      <rect x="18" y="10" width="24" height="10" rx="3" fill="#fff" opacity="0.9" />
      <circle cx="22" cy="10" r="5" fill="#fff" opacity="0.9" />
      <circle cx="30" cy="8" r="5.5" fill="#fff" opacity="0.9" />
      <circle cx="38" cy="10" r="5" fill="#fff" opacity="0.9" />
      {/* Eyes */}
      {state === 'celebrating' ? (
        <>
          <text x="21" y="33" fontSize="8" fill="#fff" textAnchor="middle">&#9733;</text>
          <text x="39" y="33" fontSize="8" fill="#fff" textAnchor="middle">&#9733;</text>
        </>
      ) : (
        <>
          <circle cx="22" cy="30" r={eyeSize} fill="#fff">
            {state === 'thinking' && <animate attributeName="cy" values="30;28;30" dur="1.5s" repeatCount="indefinite" />}
          </circle>
          <circle cx="38" cy="30" r={eyeSize} fill="#fff">
            {state === 'thinking' && <animate attributeName="cy" values="30;28;30" dur="1.5s" repeatCount="indefinite" begin="0.2s" />}
          </circle>
          {/* Pupils */}
          <circle cx="22" cy="30" r={eyeSize * 0.5} fill={color + '40'}>
            {state === 'thinking' && <animate attributeName="cx" values="22;24;20;22" dur="2s" repeatCount="indefinite" />}
          </circle>
          <circle cx="38" cy="30" r={eyeSize * 0.5} fill={color + '40'}>
            {state === 'thinking' && <animate attributeName="cx" values="38;40;36;38" dur="2s" repeatCount="indefinite" />}
          </circle>
        </>
      )}
      {/* Mouth */}
      {state === 'talking' ? (
        <ellipse cx="30" cy="42" rx={mouthW * 0.4} ry="3.5" fill="#fff" opacity="0.9">
          <animate attributeName="ry" values="3.5;2;4;2.5;3.5" dur="0.4s" repeatCount="indefinite" />
        </ellipse>
      ) : state === 'celebrating' ? (
        <path d={`M${30 - mouthW * 0.5} 40 Q30 48 ${30 + mouthW * 0.5} 40`} fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <path d={`M${30 - mouthW * 0.35} 41 Q30 45 ${30 + mouthW * 0.35} 41`} fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" opacity="0.9" />
      )}
      {/* Blush */}
      <circle cx="16" cy="37" r="3.5" fill="#ff9999" opacity="0.25" />
      <circle cx="44" cy="37" r="3.5" fill="#ff9999" opacity="0.25" />
    </svg>
  );
}

export default function ConversationalSitePage() {
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { lang, toggleLanguage } = useLanguage();
  const isAr = lang === 'ar';
  const tableNumber = searchParams.get('table');
  const [siteData, setSiteData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [sessionId] = useState(() => `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [avatarState, setAvatarState] = useState('idle');
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [thinkingText, setThinkingText] = useState('');
  const [lastIntent, setLastIntent] = useState('');
  const [idleTimer, setIdleTimer] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [menuBrowserOpen, setMenuBrowserOpen] = useState(false);
  const [menuBrowserCat, setMenuBrowserCat] = useState('all');
  // Phase 1 & 2 - Voice Agent enhancements
  const [voiceFirstMode, setVoiceFirstMode] = useState(false);
  const [realtimeMode, setRealtimeMode] = useState(false);  // NEW: OpenAI Realtime API mode
  const [silenceTimer, setSilenceTimer] = useState(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const silenceCheckRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const bgAnimRef = useRef(null); // kept for compatibility but canvas not rendered

  const cfg = siteData?.config || {};
  const c = cfg.colors || { primary: '#D97706' };
  const f = cfg.fonts || { heading: 'Tajawal', body: 'Tajawal' };
  const rName = cfg.restaurant_name || siteData?.name || 'المطعم';
  const siteLogoUrl = cfg.logo_url || '';
  const customWelcome = cfg.welcome_message || '';

  const dk = {
    bg: '#FAFAF9',
    bgSecondary: '#FFFFFF',
    glass: '#FFFFFF',
    glassBorder: '#E7E5E4',
    text: '#1C1917',
    textMuted: '#A8A29E',
    textSecondary: '#57534E',
    glow: c.primary + '30',
    glowStrong: c.primary + '50',
    inputBg: '#F5F5F4',
    subtle: '#FEF3C7',
  };

  useEffect(() => { fetchSiteData(); }, [siteId]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamingText]);
  // canvas bg removed - using CSS gradient background instead

  /* canvas background removed - replaced with CSS gradient */

  const fetchSiteData = async () => {
    try {
      const res = await axios.get(`${API}/public/site/${siteId}`);
      setSiteData(res.data.site);
      setMenuItems(res.data.menu_items || []);
      setBranchId(res.data.site?.branch_id || res.data.branch?.id || '');
      setAvatarState('idle');
      const rn = res.data.site?.config?.restaurant_name || 'المطعم';
      const customMsg = res.data.site?.config?.welcome_message || '';
      const tableMsg = tableNumber ? ` ${t('conv.all') === 'الكل' ? '🪑 طاولة رقم' : '🪑 Table #'}${tableNumber}` : '';
      const nowTime = new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
      const welcomeText = customMsg || (isAr 
          ? `يا هلا وغلا!${tableMsg} أنا ZouqBot، مساعدك في ${rn}. تبي تشوف المنيو؟ تطلب شي؟ أو عندك أي سؤال؟`
          : `Hey there!${tableMsg} I'm ZouqBot, your assistant at ${rn}. Want to see the menu? Order something? Or have any questions?`);
      setMessages([{
        role: 'assistant', text: welcomeText, rich_cards: [], id: Date.now(), time: nowTime
      }]);
    } catch {
      const nowTime = new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
      setMessages([{ role: 'assistant', text: isAr ? 'يا هلا! كيف أقدر أساعدك اليوم؟' : 'Hey! How can I help you today?', rich_cards: [], id: Date.now(), time: nowTime }]);
    } finally { setPageLoading(false); }
  };

  const streamText = useCallback((fullText, msgId) => {
    setIsStreaming(true);
    setAvatarState('talking');
    let i = 0;
    const interval = setInterval(() => {
      i += Math.floor(Math.random() * 3) + 1;
      if (i >= fullText.length) {
        i = fullText.length;
        clearInterval(interval);
        setIsStreaming(false);
        setStreamingText('');
        setAvatarState('idle');
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, text: fullText, streamed: true } : m));
      } else {
        setStreamingText(fullText.slice(0, i));
      }
    }, 25);
    return () => clearInterval(interval);
  }, []);

  const playAudio = (base64) => {
    if (!base64 || !autoSpeak) return;
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioRef.current = audio;
      setIsSpeaking(true);
      setAvatarState('talking');
      audio.onended = () => { setIsSpeaking(false); setAvatarState('idle'); };
      audio.onerror = () => { setIsSpeaking(false); setAvatarState('idle'); };
      audio.play();
    } catch { setIsSpeaking(false); }
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setIsSpeaking(false); setAvatarState('idle');
  };

  // Idle timer for proactive nudges
  const resetIdleTimer = useCallback(() => {
    if (idleTimer) clearTimeout(idleTimer);
    const timer = setTimeout(async () => {
      if (!branchId || loading) return;
      try {
        const res = await axios.get(`${API}/public/proactive-nudge?branch_id=${branchId}&session_id=${sessionId}&idle_seconds=45`);
        if (res.data.nudge) {
          setMessages(prev => [...prev, { role: 'assistant', text: res.data.text, rich_cards: [], id: Date.now(), streamed: true, isNudge: true }]);
          setSuggestedReplies(res.data.suggested_replies || []);
        }
      } catch { /* silent */ }
    }, 45000); // 45 seconds
    setIdleTimer(timer);
  }, [branchId, sessionId, loading, idleTimer]);

  // Get contextual thinking text based on intent
  const getThinkingText = useCallback((context) => {
    if (context) return context;
    const msgLower = (input || '').toLowerCase();
    if (msgLower.includes('منيو') || msgLower.includes('menu') || msgLower.includes('وريني')) return isAr ? 'يتصفح المنيو...' : 'Browsing menu...';
    if (msgLower.includes('سلة') || msgLower.includes('cart')) return isAr ? 'يراجع السلة...' : 'Checking cart...';
    if (msgLower.includes('طلب') || msgLower.includes('أكد') || msgLower.includes('order') || msgLower.includes('دفع')) return isAr ? 'يجهّز الطلب...' : 'Preparing order...';
    if (msgLower.includes('عرض') || msgLower.includes('خصم') || msgLower.includes('offer')) return isAr ? 'يبحث عن العروض...' : 'Finding offers...';
    if (msgLower.includes('تنصح') || msgLower.includes('recommend')) return isAr ? 'يختار لك الأفضل...' : 'Picking the best...';
    return isAr ? 'يفكّر...' : 'Thinking...';
  }, [input, isAr]);

  // Dynamic quick actions based on context
  const getQuickActions = useCallback(() => {
    const base = [
      { key: 'menu', icon: '🍽️', label: t('conv.quickMenu') },
    ];
    if (cart.length > 0) {
      base.push({ key: 'cart', icon: '🛒', label: t('conv.cartTitle') });
      base.push({ key: 'checkout', icon: '💳', label: isAr ? 'ادفع وأكد' : 'Pay & Confirm' });
    } else {
      base.push({ key: 'recommend', icon: '⭐', label: t('conv.quickRecommend') });
      base.push({ key: 'offers', icon: '🏷️', label: t('conv.quickOffers') });
    }
    base.push({ key: 'carousel', icon: '✨', label: t('conv.quickFeatured') });
    base.push({ key: 'branches', icon: '📍', label: t('conv.quickBranches') });
    if (lastIntent === 'place_order') {
      base.unshift({ key: 'track', icon: '📦', label: t('conv.quickTrack') });
    }
    return base;
  }, [cart, lastIntent, t, isAr]);

  const sendMessage = async (text, quickAction = '') => {
    const msg = text || input.trim();
    if (!msg && !quickAction) return;
    setInput('');
    setSuggestedReplies([]);
    const now = new Date();
    const timeStr = now.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
    if (msg) setMessages(prev => [...prev, { role: 'user', text: msg, rich_cards: [], id: Date.now(), time: timeStr }]);
    setLoading(true);
    setThinkingText(getThinkingText(''));
    setAvatarState('thinking');
    resetIdleTimer();
    try {
      const res = await axios.post(`${API}/public/smart-chat`, {
        branch_id: branchId, session_id: sessionId, message: msg || '',
        cart_items: cart.map(ci => ({ item_id: ci.id, quantity: ci.qty })),
        quick_action: quickAction,
        language: lang
      });
      const newId = Date.now() + 1;
      const respTime = new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', hour12: true });
      const hasConfetti = (res.data.rich_cards || []).some(c => c.type === 'order_confirmed');
      if (hasConfetti) setAvatarState('celebrating');

      // Sync cart from server (agent may have added/removed items via text commands)
      if (res.data.cart_sync?.length > 0) {
        setCart(res.data.cart_sync.map(ci => ({ id: ci.item_id, name: ci.name, price: ci.price, qty: ci.quantity })));
      } else if (res.data.cart_sync && res.data.cart_sync.length === 0 && res.data.actions_executed?.includes('place_order')) {
        setCart([]);
      }

      // Step 1: Show text message first (no cards yet)
      setMessages(prev => [...prev, { role: 'assistant', text: '', rich_cards: [], id: newId, streamed: false, time: respTime, pendingCards: res.data.rich_cards || [] }]);
      streamText(res.data.text, newId);

      // Step 2: After text finishes streaming, reveal cards with delay
      const textLen = (res.data.text || '').length;
      const textDelay = Math.min(textLen * 25, 1500) + 400;
      setTimeout(() => {
        setMessages(prev => prev.map(m => m.id === newId ? { ...m, rich_cards: m.pendingCards || [], pendingCards: undefined } : m));
      }, textDelay);

      // Step 3: Show suggested replies after cards
      if (res.data.suggested_replies?.length > 0) {
        setTimeout(() => setSuggestedReplies(res.data.suggested_replies.slice(0, 4)), textDelay + 600);
      }
      if (hasConfetti) { fireConfetti(); setTimeout(() => setAvatarState('idle'), 4000); }
      if (res.data.intent) setLastIntent(res.data.intent);
    } catch {
      setAvatarState('idle');
      setMessages(prev => [...prev, { role: 'assistant', text: t('conv.error'), rich_cards: [], id: Date.now(), streamed: true }]);
      setSuggestedReplies([t('conv.quickMenu'), t('conv.quickRecommend')]);
    } finally { setLoading(false); setThinkingText(''); inputRef.current?.focus(); }
  };

  // ===== LIVE ORDER STATUS TRACKING =====
  const [trackedOrderId, setTrackedOrderId] = useState(null);
  const orderWsRef = useRef(null);

  useEffect(() => {
    // Watch for order confirmation in messages to start tracking
    const lastCheckout = messages.findLast(m => m.rich_cards?.some(c => c.type === 'checkout_redirect'));
    if (lastCheckout) {
      const checkoutCard = lastCheckout.rich_cards.find(c => c.type === 'checkout_redirect');
      if (checkoutCard?.data?.checkout_id && !trackedOrderId) {
        // Check periodically if an order was created from this checkout
        const checkInterval = setInterval(async () => {
          try {
            const res = await axios.get(`${API}/public/checkout/${checkoutCard.data.checkout_id}`);
            if (res.data?.session?.status === 'paid') {
              clearInterval(checkInterval);
            }
          } catch { /* silent */ }
        }, 15000);
        return () => clearInterval(checkInterval);
      }
    }
  }, [messages, trackedOrderId]);

  // Show order status update in chat
  const showOrderStatus = useCallback((status, orderId) => {
    const statusMessages = {
      confirmed: isAr ? 'تم تأكيد طلبك! المطبخ بدأ التحضير' : 'Order confirmed! Kitchen is preparing',
      preparing: isAr ? 'طلبك قيد التحضير الآن' : 'Your order is being prepared',
      ready: isAr ? 'طلبك جاهز!' : 'Your order is ready!',
      out_for_delivery: isAr ? 'طلبك في الطريق إليك!' : 'Your order is on the way!',
      delivered: isAr ? 'تم توصيل طلبك! بالعافية' : 'Order delivered! Enjoy!',
    };
    const icons = { confirmed: '👨‍🍳', preparing: '🔥', ready: '✅', out_for_delivery: '🚗', delivered: '🎉' };
    const msg = statusMessages[status];
    if (msg) {
      setMessages(prev => [...prev, {
        role: 'system',
        text: `${icons[status] || '📦'} ${msg}`,
        rich_cards: [], id: Date.now(), isStatus: true
      }]);
    }
  }, [isAr]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // === SILENCE DETECTION via Web Audio API ===
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;

      const dataArr = new Uint8Array(analyser.fftSize);
      let silentMs = 0;
      const SILENCE_THRESHOLD = 8;
      const SILENCE_LIMIT_MS = 1600;

      const checkSilence = setInterval(() => {
        if (!analyserRef.current) { clearInterval(checkSilence); return; }
        analyserRef.current.getByteTimeDomainData(dataArr);
        let maxDeviation = 0;
        for (let i = 0; i < dataArr.length; i++) {
          maxDeviation = Math.max(maxDeviation, Math.abs(dataArr[i] - 128));
        }
        if (maxDeviation < SILENCE_THRESHOLD) {
          silentMs += 100;
          if (silentMs >= SILENCE_LIMIT_MS) {
            clearInterval(checkSilence);
            stopRecording();
          }
        } else {
          silentMs = 0;
        }
      }, 100);
      silenceCheckRef.current = checkSilence;

      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
        clearInterval(silenceCheckRef.current);
        await sendVoiceMessage(new Blob(audioChunksRef.current, { type: 'audio/webm' }));
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch { /* mic unavailable */ }
  };

  const stopRecording = () => {
    if (silenceCheckRef.current) { clearInterval(silenceCheckRef.current); silenceCheckRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    analyserRef.current = null;
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); }
  };

  const sendVoiceMessage = async (blob) => {
    setLoading(true); setAvatarState('thinking');
    setThinkingText(t('conv.thinkingDefault'));
    setSuggestedReplies([]);
    resetIdleTimer();
    const fd = new FormData();
    fd.append('audio', blob, 'voice.webm');
    fd.append('branch_id', branchId);
    fd.append('session_id', sessionId);
    fd.append('cart_items', JSON.stringify(cart.map(ci => ({ item_id: ci.id, quantity: ci.qty }))));
    try {
      const res = await axios.post(`${API}/public/voice-chat`, fd);
      if (res.data.user_text) setMessages(prev => [...prev, { role: 'user', text: res.data.user_text, rich_cards: [], id: Date.now(), isVoice: true }]);
      const newId = Date.now() + 1;
      const hasConfetti = (res.data.rich_cards || []).some(c => c.type === 'order_confirmed');
      if (hasConfetti) setAvatarState('celebrating');
      setMessages(prev => [...prev, { role: 'assistant', text: '', rich_cards: res.data.rich_cards || [], id: newId, streamed: false }]);
      streamText(res.data.text, newId);
      if (res.data.audio_base64) playAudio(res.data.audio_base64);
      if (hasConfetti) { fireConfetti(); setTimeout(() => setAvatarState('idle'), 4000); }
    } catch {
      setAvatarState('idle');
      setMessages(prev => [...prev, { role: 'assistant', text: t('conv.voiceError'), rich_cards: [], id: Date.now(), streamed: true }]);
    } finally { setLoading(false); setThinkingText(''); }
  };

  const fireConfetti = () => {
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: [c.primary, '#10B981', '#F59E0B', '#fff'] });
    setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.5 } }), 300);
  };

  const addToCart = (item) => {
    setCart(prev => {
      const ex = prev.find(ci => ci.id === item.id);
      if (ex) return prev.map(ci => ci.id === item.id ? { ...ci, qty: ci.qty + 1 } : ci);
      return [...prev, { id: item.id, name: item.name, price: item.price, qty: 1 }];
    });
    setMessages(prev => [...prev, { role: 'system', text: t('conv.addedToCart', { name: item.name }), rich_cards: [], id: Date.now() }]);
  };
  const updateQty = (id, d) => setCart(prev => prev.map(ci => ci.id === id ? { ...ci, qty: Math.max(0, ci.qty + d) } : ci).filter(ci => ci.qty > 0));
  const cartTotal = cart.reduce((s, ci) => s + ci.price * ci.qty, 0);
  const cartCount = cart.reduce((s, ci) => s + ci.qty, 0);

  /* All rich cards now rendered via RichCardRenderer from RichCards.js */

  const getMsgText = (msg) => {
    if (!msg.streamed && msg.role === 'assistant' && streamingText && messages[messages.length - 1]?.id === msg.id) return streamingText;
    return msg.text;
  };

  // Order stage for progress bar
  const orderStage = (() => {
    if (lastIntent === 'place_order' || messages.some(m => m.rich_cards?.some(c => c.type === 'order_confirmed'))) return 4;
    if (messages.some(m => m.rich_cards?.some(c => c.type === 'checkout_redirect'))) return 3;
    if (cartCount > 0) return 2;
    if (messages.length > 1) return 1;
    return 0;
  })();

  const stages = [
    { label: isAr ? 'تصفح' : 'Browse', icon: '🍽️' },
    { label: isAr ? 'اختيار' : 'Select', icon: '✨' },
    { label: isAr ? 'سلة' : 'Cart', icon: '🛒' },
    { label: isAr ? 'دفع' : 'Pay', icon: '💳' },
    { label: isAr ? 'تتبع' : 'Track', icon: '📦' },
  ];

  // Menu browser categories
  const menuCategories = [...new Set(menuItems.map(m => m.category).filter(Boolean))];
  const filteredMenuItems = menuBrowserCat === 'all' ? menuItems : menuItems.filter(m => m.category === menuBrowserCat);
  const catLabels = { main: isAr ? 'رئيسية' : 'Main', appetizer: isAr ? 'مقبلات' : 'Appetizer', dessert: isAr ? 'حلويات' : 'Dessert', beverage: isAr ? 'مشروبات' : 'Drinks', all: isAr ? 'الكل' : 'All' };

  // Welcome screen handler
  const startOrdering = () => {
    setShowWelcome(false);
    inputRef.current?.focus();
  };

  if (pageLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg, #FFFBF5 0%, #FFF8ED 50%, #FAFAF9 100%)' }}>
      <div style={{ textAlign: 'center' }}>
        <BotAvatar size={64} state="thinking" color={c.primary} />
        <div style={{ fontSize: 14, color: '#A8A29E', fontFamily: `'${f.body}', sans-serif`, marginTop: 16 }}>{t('conv.loading')}</div>
      </div>
    </div>
  );

  // ===== WELCOME SCREEN =====
  if (showWelcome) return (
    <div dir={isAr ? 'rtl' : 'ltr'} data-testid="welcome-screen" style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #FFFBF5 0%, #FFF8ED 40%, #FFFFFF 100%)', fontFamily: `'${f.body}', sans-serif`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', maxWidth: 520, margin: '0 auto', position: 'relative', overflow: 'hidden', padding: 28 }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${c.primary}12 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${c.primary}08 0%, transparent 70%)`, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
        {/* Avatar / Logo */}
        <div style={{ marginBottom: 20, animation: 'fadeSlideUp 0.6s ease both' }}>
          {siteLogoUrl ? (
            <img src={siteLogoUrl.startsWith('/') ? `${API.replace('/api', '')}${siteLogoUrl}` : siteLogoUrl} alt="" style={{ width: 88, height: 88, borderRadius: 22, objectFit: 'cover', margin: '0 auto', boxShadow: `0 8px 32px ${c.primary}25, 0 2px 8px rgba(0,0,0,0.08)` }} />
          ) : (
            <div style={{ width: 88, height: 88, borderRadius: 22, background: `linear-gradient(135deg, ${c.primary}20, ${c.primary}08)`, border: `1px solid ${c.primary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', boxShadow: `0 4px 24px ${c.primary}20` }}>
              <BotAvatar size={64} state="idle" color={c.primary} />
            </div>
          )}
        </div>

        {/* Restaurant Name */}
        <h1 style={{ fontFamily: `'${f.heading}', sans-serif`, fontWeight: 900, fontSize: 28, color: '#1C1917', margin: '0 0 6px', letterSpacing: '-0.02em', animation: 'fadeSlideUp 0.6s ease 0.1s both' }}>
          {rName}
        </h1>

        {/* Tagline */}
        <p style={{ fontSize: 14, color: '#78716C', margin: '0 0 28px', fontWeight: 500, animation: 'fadeSlideUp 0.6s ease 0.15s both' }}>
          {cfg.tagline || (isAr ? 'مرحباً! اطلب بسهولة من خلال المحادثة' : 'Welcome! Order easily through chat')}
        </p>

        {/* Quick Info Pills */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32, flexWrap: 'wrap', animation: 'fadeSlideUp 0.6s ease 0.2s both' }}>
          {tableNumber && (
            <div style={{ padding: '8px 14px', borderRadius: 20, background: `${c.primary}12`, border: `1px solid ${c.primary}25`, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14 }}>🪑</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.primary }}>{isAr ? `طاولة ${tableNumber}` : `Table ${tableNumber}`}</span>
            </div>
          )}
          <div style={{ padding: '8px 14px', borderRadius: 20, background: '#F5F5F4', border: '1px solid #E7E5E4', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>🍽️</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#78716C' }}>{menuItems.length} {isAr ? 'صنف' : 'items'}</span>
          </div>
          <div style={{ padding: '8px 14px', borderRadius: 20, background: '#F5F5F4', border: '1px solid #E7E5E4', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>💬</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#78716C' }}>{isAr ? 'دردشة + صوت' : 'Chat + Voice'}</span>
          </div>
        </div>

        {/* CTA Button */}
        <button onClick={startOrdering} data-testid="start-ordering-btn" style={{
          padding: '16px 52px', borderRadius: 28, background: `linear-gradient(135deg, ${c.primary}, ${c.primary}DD)`,
          color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 16,
          fontFamily: `'${f.body}', sans-serif`, boxShadow: `0 6px 24px ${c.primary}35`,
          transition: 'all 0.3s', animation: 'fadeSlideUp 0.6s ease 0.3s both',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 10px 32px ${c.primary}50`; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `0 6px 24px ${c.primary}35`; }}>
          {isAr ? '✨ ابدأ الطلب' : '✨ Start Ordering'}
        </button>

        {/* Browse menu link */}
        <button onClick={() => { setShowWelcome(false); setMenuBrowserOpen(true); }} data-testid="browse-menu-btn" style={{
          display: 'block', margin: '14px auto 0', padding: '10px 24px', borderRadius: 20,
          background: '#FFFFFF', border: '1px solid #E7E5E4', color: '#78716C',
          cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: `'${f.body}', sans-serif`,
          transition: 'all 0.25s', animation: 'fadeSlideUp 0.6s ease 0.4s both',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = `${c.primary}50`; e.currentTarget.style.color = c.primary; e.currentTarget.style.boxShadow = `0 2px 8px ${c.primary}15`; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#E7E5E4'; e.currentTarget.style.color = '#78716C'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}>
          🍽️ {isAr ? 'تصفح المنيو' : 'Browse Menu'}
        </button>

        {/* Voice-First Mode Button - now powered by OpenAI Realtime */}
        <button onClick={() => { setShowWelcome(false); setRealtimeMode(true); }} data-testid="voice-first-btn" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          margin: '10px auto 0', padding: '10px 24px', borderRadius: 20,
          background: `${c.primary}10`, border: `1px solid ${c.primary}25`, color: c.primary,
          cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: `'${f.body}', sans-serif`,
          transition: 'all 0.25s', animation: 'fadeSlideUp 0.6s ease 0.5s both',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `${c.primary}18`; e.currentTarget.style.boxShadow = `0 2px 12px ${c.primary}20`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${c.primary}10`; e.currentTarget.style.boxShadow = 'none'; }}>
          <Mic style={{ width: 15, height: 15 }} />
          {isAr ? '⚡ وضع صوتي فوري (AI)' : '⚡ Instant Voice (AI)'}
        </button>
      </div>
      <style>{`@keyframes fadeSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} data-testid="conversational-site" style={{ minHeight: '100vh', height: '100vh', background: 'linear-gradient(180deg, #FFFBF5 0%, #FAFAF9 100%)', fontFamily: `'${f.body}', sans-serif`, display: 'flex', flexDirection: 'column', maxWidth: 520, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>

      {/* ===== OPENAI REALTIME VOICE SESSION ===== */}
      {realtimeMode && (
        <RealtimeVoiceSession
          branchId={branchId}
          siteLogoUrl={siteLogoUrl}
          rName={rName}
          color={c.primary}
          isAr={isAr}
          sessionId={sessionId}
          onClose={() => {
            setRealtimeMode(false);
            const hasUserMsgs = messages.some(m => m.role === 'user');
            if (!hasUserMsgs) setShowWelcome(true);
          }}
        />
      )}

      {/* ===== VOICE-FIRST MODE OVERLAY ===== */}
      {voiceFirstMode && (
        <VoiceFirstMode
          isAr={isAr} color={c.primary} rName={rName} siteLogoUrl={siteLogoUrl}
          onClose={() => {
            setVoiceFirstMode(false);
            // If no user messages yet, go back to welcome screen
            const hasUserMsgs = messages.some(m => m.role === 'user');
            if (!hasUserMsgs) setShowWelcome(true);
          }}
          onSendVoice={sendVoiceMessage} loading={loading}
          isSpeaking={isSpeaking} messages={messages} avatarState={avatarState}
          isRecording={isRecording} onStartRecord={startRecording} onStopRecord={stopRecording}
          thinkingText={thinkingText}
        />
      )}
      <style>{`
        @keyframes fadeSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes menuCardEnter{from{opacity:0;transform:translateY(20px) scale(0.9) rotateX(10deg)}to{opacity:1;transform:translateY(0) scale(1) rotateX(0)}}
        @keyframes cardEnter{from{opacity:0;transform:translateY(12px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes pulse{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:.8;transform:scale(1.2)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes recordPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.4)}50%{box-shadow:0 0 0 14px rgba(239,68,68,0)}}
        @keyframes speakPulse{0%,100%{box-shadow:0 0 0 0 ${c.primary}40}50%{box-shadow:0 0 0 10px ${c.primary}00}}
        .menu-card-enter{animation:menuCardEnter 0.5s cubic-bezier(0.16,1,0.3,1) both}
        .card-enter{animation:cardEnter 0.4s cubic-bezier(0.16,1,0.3,1) both}
        .msg-enter{animation:fadeSlideUp 0.35s cubic-bezier(0.16,1,0.3,1) both}
        .custom-scrollbar::-webkit-scrollbar{height:4px;width:4px}
        .custom-scrollbar::-webkit-scrollbar-track{background:transparent}
        .custom-scrollbar::-webkit-scrollbar-thumb{background:#D6D3D1;border-radius:4px}
      `}</style>

      {/* Header */}
      <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.95)', borderBottom: '1px solid #F0EDEA', position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ animation: isSpeaking ? 'speakPulse 1.5s ease infinite' : 'none', borderRadius: 14, position: 'relative' }}>
            {siteLogoUrl ? (
              <img src={siteLogoUrl.startsWith('/') ? `${API.replace('/api', '')}${siteLogoUrl}` : siteLogoUrl} alt="" style={{ width: 44, height: 44, borderRadius: 14, objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 14, background: `linear-gradient(135deg, ${c.primary}20, ${c.primary}10)`, border: `1px solid ${c.primary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BotAvatar size={36} state={avatarState} color={c.primary} />
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#10B981', border: '2px solid #FFFFFF', boxShadow: '0 0 6px rgba(16,185,129,0.4)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: `'${f.heading}', sans-serif`, fontWeight: 800, fontSize: 16, color: '#1C1917', margin: 0, letterSpacing: '-0.01em' }}>{rName}</h1>
            <p style={{ fontSize: 11, color: '#10B981', margin: '1px 0 0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
              {t('conv.online')}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={toggleLanguage} data-testid="conv-lang-toggle"
              style={{ width: 34, height: 34, borderRadius: 10, background: '#F5F5F4', border: '1px solid #E7E5E4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716C', fontSize: 11, fontWeight: 700, transition: 'all 0.2s' }}>
              {isAr ? 'EN' : 'عربي'}
            </button>
            <button onClick={() => { if (isSpeaking) stopAudio(); setAutoSpeak(p => !p); }} data-testid="audio-toggle"
              style={{ width: 34, height: 34, borderRadius: 10, background: autoSpeak ? `${c.primary}12` : '#F5F5F4', border: `1px solid ${autoSpeak ? `${c.primary}30` : '#E7E5E4'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: autoSpeak ? c.primary : '#A8A29E', transition: 'all 0.2s' }}>
              {autoSpeak ? <Volume2 style={{ width: 15, height: 15 }} /> : <VolumeX style={{ width: 15, height: 15 }} />}
            </button>
            {cartCount > 0 && (
              <button onClick={() => setCartOpen(!cartOpen)} data-testid="cart-badge"
                style={{ position: 'relative', width: 34, height: 34, borderRadius: 10, background: `${c.primary}12`, border: `1px solid ${c.primary}25`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                <ShoppingCart style={{ width: 15, height: 15, color: c.primary }} />
                <span style={{ position: 'absolute', top: -5, [isAr ? 'right' : 'left']: -5, minWidth: 18, height: 18, borderRadius: 9, background: c.primary, color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', boxShadow: `0 2px 6px ${c.primary}50` }}>{cartCount}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Number Banner */}
      {tableNumber && !menuBrowserOpen && (
        <div data-testid="table-banner" style={{
          padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: `${c.primary}15`, borderBottom: `1px solid ${c.primary}25`, position: 'relative', zIndex: 2,
        }}>
          <span style={{ fontSize: 16 }}>🪑</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: c.primary, fontFamily: `'${f.body}', sans-serif` }}>
            {isAr ? `طاولة رقم ${tableNumber}` : `Table #${tableNumber}`}
          </span>
          <span style={{ fontSize: 10, color: '#A8A29E', fontFamily: `'${f.body}', sans-serif` }}>
            • {isAr ? 'بدون توصيل' : 'Dine-in'}
          </span>
        </div>
      )}

      {/* Order Progress Bar */}
      {!menuBrowserOpen && orderStage > 0 && (
        <div data-testid="order-progress" style={{ padding: '10px 20px', position: 'relative', zIndex: 2, borderBottom: '1px solid #F0EDEA', background: '#FFFFFF' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {stages.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < stages.length - 1 ? 1 : 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 36 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, transition: 'all 0.3s',
                    background: i <= orderStage ? `${c.primary}15` : '#F5F5F4',
                    border: `2px solid ${i <= orderStage ? c.primary : '#E7E5E4'}`,
                    boxShadow: i === orderStage ? `0 0 10px ${c.primary}25` : 'none',
                  }}>
                    {s.icon}
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: i <= orderStage ? c.primary : '#A8A29E', transition: 'color 0.3s' }}>
                    {s.label}
                  </span>
                </div>
                {i < stages.length - 1 && (
                  <div style={{ flex: 1, height: 2, margin: '0 4px', marginBottom: 16, borderRadius: 1, background: i < orderStage ? c.primary : '#E7E5E4', transition: 'background 0.3s' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== MENU BROWSER DRAWER ===== */}
      {menuBrowserOpen && (
        <div data-testid="menu-browser" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20, background: '#FAFAF9', display: 'flex', flexDirection: 'column', animation: 'fadeSlideUp 0.3s ease' }}>
          {/* Menu browser header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F0EDEA', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
            <button onClick={() => setMenuBrowserOpen(false)} data-testid="close-menu-browser"
              style={{ width: 34, height: 34, borderRadius: 10, background: '#F5F5F4', border: '1px solid #E7E5E4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716C' }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontFamily: `'${f.heading}', sans-serif`, fontWeight: 800, fontSize: 17, color: '#1C1917', margin: 0 }}>
                {isAr ? 'المنيو' : 'Menu'}
              </h2>
              <span style={{ fontSize: 11, color: '#A8A29E' }}>{menuItems.length} {isAr ? 'صنف متوفر' : 'items available'}</span>
            </div>
            {cartCount > 0 && (
              <button onClick={() => { setMenuBrowserOpen(false); setCartOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 20, background: `${c.primary}12`, border: `1px solid ${c.primary}25`, cursor: 'pointer' }}>
                <ShoppingCart style={{ width: 14, height: 14, color: c.primary }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: c.primary }}>{cartCount}</span>
                <span style={{ fontSize: 11, color: c.primary, fontWeight: 600 }}>{cartTotal} {isAr ? 'ر.س' : 'SAR'}</span>
              </button>
            )}
          </div>
          {/* Category tabs */}
          <div style={{ padding: '10px 20px', display: 'flex', gap: 8, overflowX: 'auto', borderBottom: '1px solid #F0EDEA', background: '#FFFFFF' }} className="custom-scrollbar">
            <button onClick={() => setMenuBrowserCat('all')}
              style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0, fontFamily: `'${f.body}', sans-serif`, background: menuBrowserCat === 'all' ? `${c.primary}15` : '#F5F5F4', border: `1px solid ${menuBrowserCat === 'all' ? `${c.primary}40` : '#E7E5E4'}`, color: menuBrowserCat === 'all' ? c.primary : '#78716C' }}>
              {catLabels.all} ({menuItems.length})
            </button>
            {menuCategories.map(cat => (
              <button key={cat} onClick={() => setMenuBrowserCat(cat)}
                style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0, fontFamily: `'${f.body}', sans-serif`, background: menuBrowserCat === cat ? `${c.primary}15` : '#F5F5F4', border: `1px solid ${menuBrowserCat === cat ? `${c.primary}40` : '#E7E5E4'}`, color: menuBrowserCat === cat ? c.primary : '#78716C' }}>
                {catLabels[cat] || cat} ({menuItems.filter(m => m.category === cat).length})
              </button>
            ))}
          </div>
          {/* Items grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }} className="custom-scrollbar">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {filteredMenuItems.map((item, i) => {
                const inCart = cart.find(ci => ci.id === item.id);
                return (
                  <div key={item.id} data-testid={`menu-browser-item-${item.id}`} style={{
                    borderRadius: 16, overflow: 'hidden', background: '#FFFFFF',
                    border: inCart ? `1.5px solid ${c.primary}40` : '1px solid #F0EDEA',
                    boxShadow: inCart ? `0 4px 16px ${c.primary}15` : '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s', animation: `fadeSlideUp 0.3s ease ${i * 0.03}s both`,
                  }}>
                    {item.image_url ? (
                      <div style={{ height: 100, position: 'relative', overflow: 'hidden' }}>
                        <img src={item.image_url.startsWith('/') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url} alt={item.name_ar || item.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" referrerPolicy="no-referrer" />
                        {inCart && (
                          <div style={{ position: 'absolute', top: 6, [isAr ? 'left' : 'right']: 6, padding: '3px 8px', borderRadius: 8, background: c.primary, fontSize: 10, fontWeight: 800, color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                            {inCart.qty}x
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${c.primary}10, ${c.primary}05)`, position: 'relative' }}>
                        <UtensilsCrossed style={{ width: 24, height: 24, color: `${c.primary}40` }} />
                        {inCart && (
                          <div style={{ position: 'absolute', top: 6, [isAr ? 'left' : 'right']: 6, padding: '3px 8px', borderRadius: 8, background: c.primary, fontSize: 10, fontWeight: 800, color: '#fff' }}>
                            {inCart.qty}x
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ padding: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1917', marginBottom: 3, fontFamily: `'Cairo', sans-serif` }}>{item.name_ar || item.name}</div>
                      {item.description_ar && <div style={{ fontSize: 10, color: '#A8A29E', marginBottom: 7, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.description_ar}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: c.primary }}>{item.price} <span style={{ fontSize: 10, fontWeight: 600, color: '#78716C' }}>{isAr ? 'ر.س' : 'SAR'}</span></span>
                        {inCart ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <button onClick={() => updateQty(item.id, -1)} style={{ width: 28, height: 28, borderRadius: 8, background: '#F5F5F4', border: '1px solid #E7E5E4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#57534E' }}>
                              <Minus style={{ width: 12, height: 12 }} />
                            </button>
                            <span style={{ fontSize: 14, fontWeight: 800, color: '#1C1917', minWidth: 16, textAlign: 'center' }}>{inCart.qty}</span>
                            <button onClick={() => updateQty(item.id, 1)} style={{ width: 28, height: 28, borderRadius: 8, background: `${c.primary}15`, border: `1px solid ${c.primary}25`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.primary }}>
                              <Plus style={{ width: 12, height: 12 }} />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => addToCart({ id: item.id, name: item.name_ar || item.name, price: item.price })} data-testid={`menu-add-${item.id}`}
                            style={{ width: 32, height: 32, borderRadius: 10, background: c.primary, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${c.primary}35`, transition: 'all 0.2s' }}>
                            <Plus style={{ width: 14, height: 14, color: '#fff' }} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Bottom bar */}
          {cartCount > 0 && (
            <div style={{ padding: '14px 20px', borderTop: '1px solid #F0EDEA', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -4px 16px rgba(0,0,0,0.06)' }}>
              <div>
                <div style={{ fontSize: 11, color: '#A8A29E', marginBottom: 2 }}>{isAr ? 'الإجمالي' : 'Total'}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: c.primary }}>{cartTotal} {isAr ? 'ر.س' : 'SAR'}</div>
              </div>
              <button onClick={() => { setMenuBrowserOpen(false); sendMessage('', 'checkout'); }} data-testid="menu-browser-checkout"
                style={{ padding: '12px 28px', borderRadius: 22, background: c.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: `'${f.body}', sans-serif`, boxShadow: `0 4px 16px ${c.primary}35` }}>
                {isAr ? 'أكمل الطلب' : 'Checkout'} →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Cart Drawer */}
      {cartOpen && cartCount > 0 && (
        <div data-testid="cart-drawer" className="card-enter" style={{ padding: '16px 20px', background: '#FFFFFF', borderBottom: '1px solid #F0EDEA', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingCart style={{ width: 16, height: 16, color: c.primary }} />
              <span style={{ fontFamily: `'${f.heading}', sans-serif`, fontWeight: 700, fontSize: 14, color: '#1C1917' }}>{t('conv.cartTitle')}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#A8A29E' }}>({cartCount} {t('conv.itemsCount')})</span>
            </div>
            <button onClick={() => setCartOpen(false)} style={{ background: '#F5F5F4', border: '1px solid #E7E5E4', borderRadius: 8, cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 14, height: 14, color: '#78716C' }} /></button>
          </div>
          {cart.map(ci => (
            <div key={ci.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F5F5F4' }}>
              <span style={{ fontSize: 13, color: '#1C1917', flex: 1, fontWeight: 500 }}>{ci.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => updateQty(ci.id, -1)} data-testid={`cart-minus-${ci.id}`}
                  style={{ width: 26, height: 26, borderRadius: 8, background: '#F5F5F4', border: '1px solid #E7E5E4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#57534E', transition: 'all 0.2s' }}>
                  <Minus style={{ width: 12, height: 12 }} />
                </button>
                <span style={{ fontSize: 13, fontWeight: 700, minWidth: 18, textAlign: 'center', color: '#1C1917' }}>{ci.qty}</span>
                <button onClick={() => updateQty(ci.id, 1)} data-testid={`cart-plus-${ci.id}`}
                  style={{ width: 26, height: 26, borderRadius: 8, background: `${c.primary}12`, border: `1px solid ${c.primary}25`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.primary, transition: 'all 0.2s' }}>
                  <Plus style={{ width: 12, height: 12 }} />
                </button>
                <span style={{ fontSize: 13, fontWeight: 700, color: c.primary, minWidth: 55, textAlign: isAr ? 'right' : 'left' }}>{ci.price * ci.qty} {t('conv.sar')}</span>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${c.primary}15`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#A8A29E', marginBottom: 2 }}>{t('conv.total')}</div>
              <span style={{ fontWeight: 800, color: c.primary, fontSize: 18 }}>{cartTotal} {t('conv.sar')}</span>
            </div>
            <button onClick={() => { setCartOpen(false); sendMessage('', 'checkout'); }} data-testid="cart-confirm-btn"
              style={{ padding: '10px 24px', borderRadius: 22, background: c.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: `'${f.body}', sans-serif`, boxShadow: `0 4px 14px ${c.primary}35`, transition: 'all 0.25s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = `0 6px 20px ${c.primary}50`}
              onMouseLeave={e => e.currentTarget.style.boxShadow = `0 4px 14px ${c.primary}35`}>
              {t('conv.confirmOrder')}
            </button>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, position: 'relative', zIndex: 1 }} className="custom-scrollbar">
        {messages.map((msg, i) => (
          <div key={msg.id} className="msg-enter" style={{ animationDelay: `${Math.min(i * 0.04, 0.2)}s` }}>
            {msg.role === 'system' ? (
              <div data-testid={msg.isStatus ? 'order-status-msg' : 'system-msg'} style={{
                textAlign: 'center', padding: msg.isStatus ? '10px 16px' : '5px 16px', fontSize: msg.isStatus ? 13 : 11,
                color: msg.isStatus ? '#1C1917' : c.primary, fontWeight: 600,
                background: msg.isStatus ? `${c.primary}10` : 'transparent',
                borderRadius: msg.isStatus ? 12 : 0,
                border: msg.isStatus ? `1px solid ${c.primary}20` : 'none',
                margin: msg.isStatus ? '4px 20px' : 0,
              }}>
                {!msg.isStatus && <Sparkles style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle', marginLeft: 4 }} />} {msg.text}
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end', gap: 8, alignItems: 'flex-end' }}>
                {msg.role === 'assistant' && (
                  <div style={{ marginBottom: 2, flexShrink: 0 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 10, background: `linear-gradient(135deg, ${c.primary}20, ${c.primary}10)`, border: `1px solid ${c.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BotAvatar size={22} state={!msg.streamed && messages[messages.length - 1]?.id === msg.id ? avatarState : 'idle'} color={c.primary} />
                    </div>
                  </div>
                )}
                <div style={{ maxWidth: '80%' }}>
                  <div style={{
                    padding: '12px 16px', fontSize: 14, lineHeight: 1.7, fontFamily: `'${f.body}', sans-serif`,
                    borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '4px 20px 20px 20px',
                    background: msg.role === 'user'
                      ? `linear-gradient(135deg, ${c.primary}, ${c.primary}E0)`
                      : msg.isNudge ? `${c.primary}08` : '#FFFFFF',
                    color: msg.role === 'user' ? '#FFFFFF' : '#1C1917',
                    boxShadow: msg.role === 'user'
                      ? `0 3px 12px ${c.primary}30`
                      : msg.isNudge ? `0 2px 8px ${c.primary}10` : '0 2px 10px rgba(0,0,0,0.06)',
                    border: msg.role === 'user' ? 'none' : msg.isNudge ? `1px solid ${c.primary}20` : '1px solid #F0EDEA',
                  }}>
                    {getMsgText(msg)}
                    {!msg.streamed && msg.role === 'assistant' && isStreaming && messages[messages.length - 1]?.id === msg.id && (
                      <span style={{ display: 'inline-block', width: 2, height: 14, background: c.primary, marginRight: 2, verticalAlign: 'middle', animation: 'blink 0.8s step-end infinite' }} />
                    )}
                    {msg.isVoice && <Mic style={{ width: 11, height: 11, display: 'inline', verticalAlign: 'middle', marginRight: 6, opacity: 0.5 }} />}
                  </div>
                  {/* Timestamp */}
                  {msg.time && (
                    <div data-testid={`msg-time-${msg.id}`} style={{ fontSize: 9, color: msg.role === 'user' ? `${c.primary}80` : '#C1BAB4', marginTop: 3, [msg.role === 'user' ? (isAr ? 'marginLeft' : 'marginRight') : (isAr ? 'marginRight' : 'marginLeft')]: 4, fontWeight: 500 }}>
                      {msg.time}
                    </div>
                  )}
                  {msg.rich_cards?.map((card, j) => <div key={j} className="card-enter" style={{ animationDelay: `${j * 0.15}s` }}>
                    <RichCardRenderer card={card} color={c.primary} onAddToCart={(item, qty) => addToCart(item)} />
                  </div>)}
                </div>
              </div>
            )}
          </div>
        ))}
        {loading && !isStreaming && (
          <div className="msg-enter" data-testid="typing-indicator" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ width: 30, height: 30, borderRadius: 10, background: `linear-gradient(135deg, ${c.primary}20, ${c.primary}10)`, border: `1px solid ${c.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BotAvatar size={22} state="thinking" color={c.primary} />
            </div>
            <div style={{ padding: '12px 18px', borderRadius: '4px 20px 20px 20px', background: '#FFFFFF', border: '1px solid #F0EDEA', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: c.primary, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
                </div>
                <span style={{ fontSize: 12, color: '#A8A29E', fontFamily: `'${f.body}', sans-serif`, fontWeight: 600 }}>
                  {thinkingText || (isAr ? 'يكتب...' : 'Typing...')}
                </span>
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Suggested Replies */}
      {suggestedReplies.length > 0 && !loading && (
        <div data-testid="suggested-replies" style={{ padding: '6px 20px 2px', display: 'flex', gap: 8, overflowX: 'auto', position: 'relative', zIndex: 2 }} className="custom-scrollbar">
          {suggestedReplies.map((reply, i) => (
            <button key={i} onClick={() => { setSuggestedReplies([]); sendMessage(reply); }} data-testid={`suggested-reply-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', borderRadius: 20,
                background: '#FFFFFF', border: `1px solid ${c.primary}30`, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, color: c.primary, fontFamily: `'${f.body}', sans-serif`,
                whiteSpace: 'nowrap', transition: 'all 0.2s', flexShrink: 0,
                animation: `fadeSlideUp 0.3s cubic-bezier(0.16,1,0.3,1) ${i * 0.08}s both`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${c.primary}10`; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 3px 10px ${c.primary}20`; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}>
              {reply}
            </button>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ padding: '8px 20px 4px', display: 'flex', gap: 8, overflowX: 'auto', position: 'relative', zIndex: 2 }} className="custom-scrollbar">
        {/* Menu browser button */}
        <button onClick={() => setMenuBrowserOpen(true)} data-testid="open-menu-browser"
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20,
            background: `${c.primary}12`, border: `1px solid ${c.primary}25`, cursor: 'pointer',
            fontSize: 12, fontWeight: 700, color: c.primary, fontFamily: `'${f.body}', sans-serif`,
            whiteSpace: 'nowrap', transition: 'all 0.2s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${c.primary}20`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${c.primary}12`; e.currentTarget.style.transform = ''; }}>
          <UtensilsCrossed style={{ width: 13, height: 13 }} /> {isAr ? 'المنيو' : 'Menu'}
        </button>
        {getQuickActions().map(qa => (
          <button key={qa.key} onClick={() => sendMessage('', qa.key)} disabled={loading} data-testid={`quick-${qa.key}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20,
              background: '#FFFFFF', border: '1px solid #E7E5E4', cursor: loading ? 'default' : 'pointer',
              fontSize: 12, fontWeight: 600, color: '#57534E', fontFamily: `'${f.body}', sans-serif`,
              whiteSpace: 'nowrap', transition: 'all 0.2s', flexShrink: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)', opacity: loading ? 0.6 : 1,
            }}
            onMouseEnter={e => { if(!loading) { e.currentTarget.style.background = `${c.primary}08`; e.currentTarget.style.borderColor = `${c.primary}30`; e.currentTarget.style.color = c.primary; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 3px 10px ${c.primary}15`; }}}
            onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#E7E5E4'; e.currentTarget.style.color = '#57534E'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}>
            <span style={{ fontSize: 13 }}>{qa.icon}</span> {qa.label}
          </button>
        ))}
      </div>

      {/* Input Bar */}
      <div style={{ padding: '10px 20px', paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))', background: '#FFFFFF', borderTop: '1px solid #F0EDEA', boxShadow: '0 -4px 16px rgba(0,0,0,0.04)', position: 'relative', zIndex: 2 }}>
        {/* Waveform while recording */}
        {isRecording && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '6px 0 10px', animation: 'fadeSlideUp 0.3s ease' }}>
            <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 600, animation: 'pulse 1.5s ease infinite' }}>
              {isAr ? '🎙️ جاري التسجيل...' : '🎙️ Recording...'}
            </div>
            <VoiceWaveform isActive={true} color="#EF4444" barCount={16} height={28} />
            <div style={{ fontSize: 10, color: '#A8A29E' }}>{isAr ? 'توقف تلقائي' : 'Auto-stop'}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onMouseDown={startRecording} onMouseUp={stopRecording} onMouseLeave={() => isRecording && stopRecording()}
            onTouchStart={startRecording} onTouchEnd={stopRecording} disabled={loading} data-testid="voice-btn"
            style={{
              width: 42, height: 42, borderRadius: '50%', border: `1px solid ${isRecording ? '#EF4444' : '#E7E5E4'}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: isRecording ? 'linear-gradient(135deg, #EF4444, #DC2626)' : '#F5F5F4',
              color: isRecording ? '#fff' : '#A8A29E', transition: 'all 0.2s',
              animation: isRecording ? 'recordPulse 1.5s ease infinite' : 'none',
            }}>
            {isRecording ? <MicOff style={{ width: 17, height: 17 }} /> : <Mic style={{ width: 17, height: 17 }} />}
          </button>
          <div style={{ flex: 1, position: 'relative' }}>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={isRecording ? (isAr ? '🎙️ جاري الاستماع...' : '🎙️ Listening...') : t('conv.inputPlaceholder')} disabled={loading || isRecording} data-testid="conv-chat-input"
              style={{ width: '100%', height: 42, borderRadius: 21, border: '1.5px solid #E7E5E4', padding: '0 20px', fontSize: 14, fontFamily: `'${f.body}', sans-serif`, background: isRecording ? '#FFF5F5' : '#F5F5F4', color: '#1C1917', outline: 'none', transition: 'all 0.2s' }}
              onFocus={e => { e.target.style.borderColor = `${c.primary}70`; e.target.style.background = '#FFFFFF'; e.target.style.boxShadow = `0 0 0 3px ${c.primary}12`; }}
              onBlur={e => { e.target.style.borderColor = '#E7E5E4'; e.target.style.background = isRecording ? '#FFF5F5' : '#F5F5F4'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} data-testid="conv-chat-send"
            style={{ width: 42, height: 42, borderRadius: '50%', background: (!input.trim() || loading) ? '#E7E5E4' : c.primary, color: '#fff', border: 'none', cursor: (!input.trim() || loading) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: (!input.trim() || loading) ? 'none' : `0 3px 12px ${c.primary}40`, flexShrink: 0, transition: 'all 0.25s' }}>
            <Send style={{ width: 17, height: 17 }} />
          </button>
        </div>
        {/* Realtime Voice Mode button (shows when idle) */}
        {!input && !isRecording && !loading && (
          <button onClick={() => setRealtimeMode(true)} data-testid="switch-voice-mode"
            style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '6px auto 0', padding: '4px 12px', borderRadius: 20, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 10, color: c.primary, fontWeight: 600 }}>
            <Phone style={{ width: 10, height: 10 }} />
            {isAr ? '⚡ جرّب الوضع الصوتي الفوري' : '⚡ Try Instant Voice Mode'}
          </button>
        )}
      </div>
    </div>
  );
}
